import React from "react";
import { Link } from "react-router-dom";
import MarketingNav from "@/components/MarketingNav";
import QuoteCTA from "@/components/QuoteCTA";
import Seo from "@/components/Seo";
import SiteFooter from "@/components/SiteFooter";
import { BIZ } from "@/data/site";
import { ShieldCheck, MapPin, Handshake, Sparkles, User, Truck } from "lucide-react";

export default function AboutPage() {
  return (
    <div className="bg-paper min-h-screen">
      <Seo
        title="About Wynridge Exterior Cleaning"
        description={`Locally-owned pressure washing and soft washing serving ${BIZ.region} — Licensed & Insured. Meet the owner.`}
        path="/about/"
      />
      <MarketingNav />
      <QuoteCTA />

      <section className="pt-6 pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-12 items-start">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-700 mb-4">About us</div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-stone-900 leading-[1.05]" style={{ fontFamily: "Outfit" }}>
              Local, careful,<br /><span className="text-electric-500 italic">and always insured.</span>
            </h1>
            <p className="mt-6 text-lg text-stone-600 leading-relaxed">
              Wynridge Exterior Cleaning is a locally-owned exterior cleaning company based in {BIZ.city}, {BIZ.stateShort}. We built the business around one idea: professional-grade equipment plus a careful, no-shortcut process is what actually delivers a clean home. Right pressure, right detergent, right result.
            </p>
            <p className="mt-4 text-lg text-stone-600 leading-relaxed">
              We serve homeowners, HOAs, and small businesses across {BIZ.region}. Every visit closes with a photo report and a satisfaction walk-through.
            </p>
            <p className="mt-4 text-sm text-stone-500 italic">
              Owner note: Replace this section with your personal story, family details, or how the business got started.
            </p>
          </div>

          <div className="rounded-3xl overflow-hidden bg-gradient-to-br from-stone-200 to-stone-300 aspect-square flex flex-col items-center justify-center text-stone-500 border border-stone-200" data-testid="owner-photo-slot">
            <User className="w-12 h-12" />
            <div className="text-xs mt-3 uppercase tracking-widest font-bold">[Owner photo]</div>
            <div className="text-[10px] mt-1 opacity-60">upload later</div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-white border-y border-stone-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <TrustBlock icon={ShieldCheck} title="Licensed & Insured" body="Full liability coverage. Certificate of insurance available on request for HOA and commercial jobs." />
          <TrustBlock icon={MapPin} title="Local to Goodlettsville" body="Service-area business — we live and work in Middle Tennessee. No franchise, no out-of-town crews." />
          <TrustBlock icon={Handshake} title="Honest Work, Fair Prices" body="Upfront quotes in writing. No pressure. No upsell on the truck." />
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="rounded-3xl aspect-video bg-gradient-to-br from-brand-100 to-brand-200 flex flex-col items-center justify-center text-brand-700 border border-brand-200">
            <Truck className="w-12 h-12" />
            <div className="text-xs mt-3 uppercase tracking-widest font-bold">[Service vehicle photo]</div>
            <div className="text-[10px] mt-1 opacity-70">upload later</div>
          </div>
          <div className="rounded-3xl aspect-video bg-gradient-to-br from-stone-200 to-stone-300 flex flex-col items-center justify-center text-stone-500 border border-stone-200">
            <Sparkles className="w-12 h-12" />
            <div className="text-xs mt-3 uppercase tracking-widest font-bold">[Equipment photo]</div>
            <div className="text-[10px] mt-1 opacity-60">upload later</div>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

function TrustBlock({ icon: Icon, title, body }) {
  return (
    <div className="p-6 rounded-2xl border border-stone-200 bg-stone-50">
      <div className="w-11 h-11 rounded-xl bg-brand-600 flex items-center justify-center">
        <Icon className="w-5 h-5 text-white" />
      </div>
      <h3 className="mt-4 font-bold text-lg text-stone-900" style={{ fontFamily: "Outfit" }}>{title}</h3>
      <p className="mt-2 text-sm text-stone-600 leading-relaxed">{body}</p>
    </div>
  );
}
